import type { CleaningTask, ScheduleType, ScheduleValue, TaskInput } from "../types";
import { daysBetween, toUtcDate, todayInJapan } from "./date";

export const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

export function parseScheduleValue(value: string): ScheduleValue {
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as ScheduleValue) : {};
  } catch {
    return {};
  }
}

export function isScheduledForDate(task: CleaningTask, isoDate: string): boolean {
  const value = parseScheduleValue(task.schedule_value);
  const weekday = toUtcDate(isoDate).getUTCDay();

  switch (task.schedule_type) {
    case "daily":
      return true;
    case "weekly":
      return value.weekday === weekday;
    case "weekdays":
      return value.weekdays?.includes(weekday) ?? false;
    case "interval": {
      if (!value.startDate || !value.days || value.days < 2) return false;
      const difference = daysBetween(value.startDate, isoDate);
      return difference >= 0 && difference % value.days === 0;
    }
    default:
      return false;
  }
}

export function scheduleLabel(task: Pick<CleaningTask, "schedule_type" | "schedule_value">): string {
  const value = parseScheduleValue(task.schedule_value);
  switch (task.schedule_type) {
    case "daily":
      return "毎日";
    case "weekly":
      return `毎週${WEEKDAYS[value.weekday ?? 1]}曜日`;
    case "weekdays":
      return (value.weekdays ?? []).map((day) => WEEKDAYS[day]).join("・") + "曜日";
    case "interval":
      return `${value.days ?? 2}日ごと`;
    default:
      return "周期なし";
  }
}

export function defaultTaskInput(): TaskInput {
  return { name: "", scheduleType: "none", scheduleValue: {} };
}

export function inputFromTask(task: CleaningTask): TaskInput {
  return {
    name: task.name,
    scheduleType: task.schedule_type,
    scheduleValue: parseScheduleValue(task.schedule_value),
  };
}

export function normalizeScheduleValue(type: ScheduleType, value: ScheduleValue): ScheduleValue {
  switch (type) {
    case "weekly":
      return { weekday: value.weekday ?? 1 };
    case "weekdays":
      return { weekdays: value.weekdays?.length ? [...new Set(value.weekdays)].sort() : [1] };
    case "interval":
      return {
        days: Math.min(365, Math.max(2, value.days ?? 2)),
        startDate: value.startDate ?? todayInJapan(),
      };
    default:
      return {};
  }
}
